import { RadioCardGroup } from "@blurb/codex-react";
import { Box, createStyles, Stack } from "@mantine/core";
import { RpiAlert, RpiText } from "@rpi/openapi-core";
import {
  attributesForFiltering,
  invalidateSubsequentSteps,
  NewProductState,
  ProductCreateNavButtons,
  StepProps,
} from "./ProductCreateNavigator";
import {
  filterByMatchingAttributes,
  findAvailableAttributeCombinations,
  ProductAttributes,
  serializeCombination,
  StandardProduct,
} from "../../hooks/useStandardProducts";
import { ReactNode, useState } from "react";

type ChoiceStepProps = StepProps & {
  instructionText?: ReactNode;
  noOptionsText?: string;
  standardProducts: StandardProduct[];
  flexDirection?: "row" | "column";
  isLoading?: boolean;
  renderCard: (combination: ProductAttributes, id: string) => JSX.Element | null;
  renderImage?: (combination: ProductAttributes, id: string) => JSX.Element | null;
  sortCombinations?: (combinations: ProductAttributes[]) => ProductAttributes[];
  getFilterAttributes?: (state: StepProps["state"]) => Record<string, string | number | boolean | undefined>;
  onAdditionalValidation?: () => Promise<ReactNode | undefined>;
};

const useCardStepStyles = createStyles((theme, { flexDirection }: { flexDirection: "row" | "column" }) => ({
  cardGrid: {
    display: "flex",
    gap: "var(--codex-spacing-2)",
    marginTop: "var(--codex-spacing-8)",
    marginBottom: "var(--codex-spacing-2)",
    flexDirection,
    flexWrap: "wrap",
  },
}));

// Generic card step component. A step owns one or more attributes; each card is a distinct combination of their values.
export function ChoiceStep({
  state,
  setState,
  attributeKeys,
  instructionText = "Please select one to continue.",
  noOptionsText = "No options available based on previous selections.",
  standardProducts,
  visibleSteps,
  renderCard,
  renderImage,
  flexDirection = "row",
  getFilterAttributes,
  sortCombinations,
  onAdditionalValidation,
  isLoading,
}: ChoiceStepProps) {
  const { classes } = useCardStepStyles({ flexDirection });
  const [errorMessage, setErrorMessage] = useState<ReactNode>("");

  if (attributeKeys.length === 0) return <RpiAlert>Configuration error: No attribute keys.</RpiAlert>;

  // Use custom filter attributes or default to attributesForFiltering
  const filterAttributes = getFilterAttributes
    ? getFilterAttributes(state)
    : attributesForFiltering(state, visibleSteps);
  const filteredProducts = filterByMatchingAttributes(standardProducts, filterAttributes);
  const combinations = findAvailableAttributeCombinations(filteredProducts, attributeKeys);
  const sortedCombinations = sortCombinations ? sortCombinations(combinations) : combinations;

  const currentCombination: ProductAttributes = Object.fromEntries(
    attributeKeys.map((key) => [key, state.attributes[key]])
  );
  const currentValue = attributeKeys.every((key) => state.attributes[key] !== undefined)
    ? serializeCombination(attributeKeys, currentCombination)
    : "";

  const handleSelect = (id: string) => {
    const selected = combinations.find((combination) => serializeCombination(attributeKeys, combination) === id);
    if (!selected) return;
    setState((draft) => {
      for (const key of attributeKeys) {
        draft.attributes[key] = selected[key];
      }
      draft.nextFailed = false;
      invalidateSubsequentSteps(draft, draft.currentStepIndex, visibleSteps);
    });
  };

  const handleNext = async () => {
    const validationError = validateSelection(attributeKeys, state) || (await onAdditionalValidation?.());

    if (validationError) {
      setErrorMessage(validationError);
      setState((draft) => {
        draft.nextFailed = true;
      });
    } else {
      setErrorMessage("");
      setState((draft) => {
        draft.completedStepIndex = draft.currentStepIndex;
        draft.currentStepIndex += 1;
        draft.nextFailed = false;
      });
    }
  };

  return (
    <Stack spacing={0}>
      <RpiText>{instructionText}</RpiText>

      {combinations.length === 0 ? (
        <RpiAlert>{noOptionsText}</RpiAlert>
      ) : (
        <RadioCardGroup value={currentValue} onValueChange={(value) => handleSelect(String(value))}>
          <Box className={classes.cardGrid}>
            {sortedCombinations.map((combination) =>
              renderCard(combination, serializeCombination(attributeKeys, combination))
            )}
          </Box>
        </RadioCardGroup>
      )}

      <Box sx={{ marginBottom: "var(--codex-spacing-6)" }}>
        {currentValue && renderImage && renderImage(currentCombination, currentValue)}
      </Box>
      {state.nextFailed && errorMessage && <RpiAlert>{errorMessage}</RpiAlert>}

      <ProductCreateNavButtons
        state={state}
        setState={setState}
        visibleSteps={visibleSteps}
        onNext={handleNext}
        loading={isLoading}
      />
    </Stack>
  );
}

const noSelectionErrorText = "Please make a selection to continue.";
function validateSelection(attributeKeys: string[], state: NewProductState) {
  if (attributeKeys.some((key) => state.attributes[key] === undefined)) {
    return noSelectionErrorText;
  }
}
